<template>
  <div
    v-if="sale"
    class="mt-auto bg-gray-100 -m-6 pt-6 pb-8 px-4 md:px-6 rounded-md print:bg-white print:mt-0 print:pt-0 print:min-h-screen print:overflow-visible"
  >
    <div class="bg-white rounded-md overflow-hidden h-full print:min-h-screen print:overflow-visible modal">
      <div
        class="block sm:flex items-stretch text-white print:flex print:text-black print:border-b-2 print:border-gray-300 print:rounded-md"
      >
        <div class="flex-1 bg-black p-8">
          <div class="uppercase text-4xl font-extrabold">{{ $t('Invoice') }}</div>
          <div v-if="$company?.gst" class="font-bold">{{ $t('GST') }}: {{ $company.gst }}</div>
        </div>
        <div class="w-full sm:w-1/4 min-w-fit bg-gray-900 p-8 text-center print:text-right print:pr-24">
          {{ $t('Amount') }} ({{ $settings.currency_code }})
          <div class="text-center text-4xl font-bold print:text-right">{{ $currency(sale.grand_total) }}</div>
        </div>
      </div>

      <div class="block sm:flex justify-between print:flex p-8">
        <div class="text-left w-full sm:w-1/2">
          <div class="text-gray-600">{{ $t('Billed to') }}:</div>
          <div v-if="sale.customer?.company" class="font-bold">{{ sale.customer.company }}</div>
          <div :class="sale.customer?.company ? '' : 'font-bold'">{{ sale.customer.name }}</div>
          <div v-if="sale.customer?.phone">{{ $t('Tel') }}: {{ sale.customer.phone }}</div>
          <div v-if="sale.customer?.email">{{ $t('Email') }}: {{ sale.customer.email }}</div>
          <div v-if="sale.customer?.address || sale.customer?.city || sale.customer?.state || sale.customer?.country">
            {{ $t('Address') }}: {{ sale.customer.address || '' }} {{ sale.customer.city || '' }} {{ sale.customer.postal_code || '' }}
            {{ sale.customer.state || '' }} {{ sale.customer.country || '' }}
          </div>
        </div>
        <div class="w-full sm:w-1/2 mt-4 sm:mt-0 flex justify-end">
          <table>
            <tr>
              <td class="text-right px-2 font-bold">{{ $t('Invoice Number') }}:</td>
              <td class="px-2">{{ sale.number }}</td>
            </tr>
            <tr>
              <td class="text-right px-2 font-bold">{{ $t('Locator Number') }}:</td>
              <td class="px-2">{{ sale.locator_number }}</td>
            </tr>
            <tr>
              <td class="text-right px-2 font-bold">{{ $t('Invoice Date') }}:</td>
              <td class="px-2">{{ $date(sale.date) }}</td>
            </tr>
            <tr>
              <td class="text-right px-2 font-bold">{{ $t('Created at') }}:</td>
              <td class="px-2">{{ $datetime(sale.created_at) }}</td>
            </tr>
          </table>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full mt-8 mb-4" style="min-width: 650px">
          <thead>
            <tr>
              <!-- <th scope="col" class="py-3.5 pl-4 pr-3 text-center text-sm font-semibold text-gray-900 sm:pl-6 w-12">No.</th> -->
              <th scope="col" class="text-left text-sm pl-5 font-bold text-gray-900">
                <div class="px-3 py-2 w-full min-w-[150px]">{{ $t('Description') }}</div>
              </th>
              <th scope="col" class="text-center text-sm font-bold text-gray-900">
                <div class="px-3 py-2 w-24">{{ $t('Quantity') }}</div>
              </th>
              <th scope="col" class="text-center text-sm font-bold text-gray-900">
                <div class="px-3 py-2 w-24">{{ $t('Tax') }}</div>
              </th>
              <th scope="col" class="text-center text-sm font-bold text-gray-900">
                <div class="px-3 py-2 w-24">{{ $t('HST') }}</div>
              </th>
              <template v-if="$can('show-details')">
                <th scope="col" :class="print_details ? 'p-0' : 'p-0 w-0'" class="text-center text-sm font-bold text-gray-900">
                  <div :class="print_details ? 'px-3 py-2 w-24' : 'print:hidden px-3 py-3.5 w-24'">{{ $t('Agent Commission') }}</div>
                </th>
                <th scope="col" :class="print_details ? 'p-0' : 'p-0 w-0'" class="text-center text-sm font-bold text-gray-900">
                  <div :class="print_details ? 'px-3 py-3.5 w-24' : 'print:hidden px-3 py-3.5 w-24'">{{ $t('Commission') }}</div>
                </th>
                <th scope="col" :class="print_details ? 'p-0' : 'p-0 w-0'" class="text-center text-sm font-bold text-gray-900">
                  <div :class="print_details ? 'px-3 py-3.5 w-24' : 'print:hidden px-3 py-3.5 w-24'">{{ $t('HST on Comm.') }}</div>
                </th>
              </template>
              <th scope="col" class="pl-3 pr-8 py-2 text-center text-sm font-bold text-gray-900 w-40">{{ $t('Airfare') }}</th>
              <!-- <th scope="col" class="px-3 py-3.5 text-left text-sm font-bold text-gray-900 w-40">{{$t('Total')}}</th> -->
            </tr>
          </thead>

          <template v-for="(item, ii) in sale.items" :key="'i_' + item.id">
            <tbody class="group avoid" :class="{ 'bg-gray-50': ii % 2 != 0, 'border-b': sale.items.length == ii + 1 }">
              <tr>
                <!-- <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold">{{ ii + 1 }}</td> -->
                <td class="group-hover:bg-gray-100 border-t pr-6 py-2 pl-8">{{ $t(item.description) }}</td>
                <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-center">
                  {{ $number(item.quantity, null, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) }}
                </td>
                <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-right">{{ $number(item.tax) }}</td>
                <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-right">{{ $number(item.hst) }}</td>
                <template v-if="$can('show-details')">
                  <td class="group-hover:bg-gray-100 border-t text-right" :class="print_details ? 'px-6 py-2' : 'p-0 w-0'">
                    <span :class="{ 'print:hidden': !print_details }">{{ $number(item.agent_commission) }}</span>
                  </td>
                  <td class="group-hover:bg-gray-100 border-t text-right" :class="print_details ? 'px-6 py-2' : 'p-0 w-0'">
                    <span :class="{ 'print:hidden': !print_details }">{{ $number(item.commission) }}</span>
                  </td>
                  <td class="group-hover:bg-gray-100 border-t text-right" :class="print_details ? 'px-6 py-2' : 'p-0 w-0'">
                    <span :class="{ 'print:hidden': !print_details }">{{ $number(item.hst_on_commission) }}</span>
                  </td>
                </template>
                <!-- <template v-if="$can('show-details')"> -->
                <td class="group-hover:bg-gray-100 border-t pl-6 pr-8 py-2 text-right">
                  {{ $number(Number(item.airfare) + Number(item.commission)) }}
                </td>
                <!-- </template>
                <template v-else>
                  <td class="group-hover:bg-gray-100 border-t pl-6 pr-8 py-2 text-right">
                    {{ $number(item.total) }}
                  </td>
                </template> -->
                <!-- <td class="group-hover:bg-gray-100 border-t px-6 py-2 text-right">{{ $number(item.total) }}</td> -->
              </tr>
            </tbody>
          </template>

          <tfoot>
            <tr>
              <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold text-right" :colspan="$can('show-details') ? 7 : 5">
                {{ $t('Airfare') }}
              </td>
              <td class="group-hover:bg-gray-100 border-t pl-6 pr-8 py-2 font-bold text-right">{{ $number(sale.total) }}</td>
            </tr>
            <tr>
              <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold text-right" :colspan="$can('show-details') ? 7 : 5">
                {{ $t('Total Tax') }}
              </td>
              <td class="group-hover:bg-gray-100 border-t pl-6 pr-8 py-2 font-bold text-right">{{ $number(sale.total_tax) }}</td>
            </tr>
            <tr>
              <td class="group-hover:bg-gray-100 border-t px-6 py-2 font-bold text-right" :colspan="$can('show-details') ? 7 : 5">
                {{ $t('Grand Total') }}
              </td>
              <td class="group-hover:bg-gray-100 border-t pl-6 pr-8 py-2 font-bold text-right">{{ $number(sale.grand_total) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <dl class="p-8 flex flex-wrap gap-y-4">
        <div class="w-full sm:w-1/2 print:w-1/2">
          <dt class="text-sm font-medium text-gray-500">{{ $t('Status') }}</dt>
          <dd class="mt-1 text-sm text-gray-900"><Status :status="sale.status" /></dd>
        </div>
        <div class="w-full sm:w-1/2 print:w-1/2">
          <dt class="text-sm font-medium text-gray-500">{{ $t('Payment Method') }}</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ $t(sale.payment_method) }}</dd>
        </div>
        <template v-if="sale.cc_fees">
          <div class="w-full sm:w-1/2 print:w-1/2">
            <dt class="text-sm font-medium text-gray-500">{{ $t('Credit Card Fee') }}</dt>
            <dd class="mt-1 text-sm text-gray-900">{{ $number(sale.cc_fees) }}</dd>
          </div>
          <div class="w-full sm:w-1/2 print:w-1/2">
            <dt class="text-sm font-medium text-gray-500"></dt>
            <dd class="mt-1 text-sm text-gray-900"></dd>
          </div>
        </template>
        <div class="w-full sm:w-1/2 print:w-1/2">
          <dt class="text-sm font-medium text-gray-500">{{ $t('Medical Insurance') }}</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ sale.medical_insurance == 1 ? 'Yes' : 'No' }}</dd>
        </div>
        <div class="w-full sm:w-1/2 print:w-1/2">
          <dt class="text-sm font-medium text-gray-500">{{ $t('Travel Insurance') }}</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ sale.travel_insurance == 1 ? 'Yes' : 'No' }}</dd>
        </div>
      </dl>

      <div v-if="sale.comment" class="p-8 w-full">{{ $t(sale.comment) }}</div>
      <div v-if="$settings.default_sale_comment" class="p-8 w-full text-sm">{{ $settings.default_sale_comment }}</div>

      <div class="mt-auto block sm:flex justify-between border-t print:flex p-8">
        <div class="block sm:flex justify-between gap-8 print:flex">
          <div v-if="$settings.invoice_logo_url">
            <div class="my-2 w-80 max-h-[100px] flex items-start justify-start">
              <img :src="$settings.invoice_logo_url" :alt="$settings.name" class="max-h-full max-w-full rounded object-cover" />
            </div>
          </div>
          <tec-application-logo v-else class="mb-4 sm:mb-0" />
          <div class="w-full sm:w-48 text-left leading-snug max-w-md print:text-right">
            <div class="font-bold">{{ $company.company_name }}</div>
            <!-- <div v-if="$company?.gst" class="font-bold">GST: {{ $company.gst }}</div>
          <div v-if="$company?.vat" class="font-bold">VAT: {{ $company.vat }}</div> -->
            <div>
              <span class="mr-1" v-if="$company?.address">{{ $company.address }}</span>
              <span class="mr-1" v-if="$company?.city">{{ $company.city }}</span>
              <span class="mr-1" v-if="$company?.state">{{ $company.state }}</span>
              <span class="mr-1" v-if="$company?.postal_code">{{ $company.postal_code }}</span>
              <span v-if="$company?.country">{{ $company.country }}</span>
            </div>
          </div>
        </div>
        <div class="w-full sm:w-48 mt-4 sm:mt-0 text-right leading-snug max-w-md print:text-right">
          <div class="font-bold">{{ $t('Contact Information') }}</div>
          <div v-if="$company?.phone">{{ $t('Tel') }}: {{ $company.phone }}</div>
          <div v-if="$company?.email">{{ $t('Email') }}: {{ $company.email }}</div>
        </div>
      </div>
    </div>

    <!-- <div class="py-4 w-full">
          <h1 class="text-xl text-center uppercase font-extrabold">{{ $t('Sale') }}</h1>
          <svg id="barcode" class="mt-2 mx-auto"></svg>
        </div> -->

    <div class="mt-auto pt-4 w-full text-center text-sm text-gray-500 hidden print:block">
      {{ $t('This is computer generated document, no signature required.') }}
    </div>
  </div>
</template>

<script>
// import JsBarcode from 'jsbarcode';
import Status from './Status.vue';
import TrashedMessage from '@/Shared/TrashedMessage.vue';
import TecApplicationLogo from '@/Jetstream/ApplicationLogo.vue';

export default {
  props: { sale: Object },
  components: { Status, TrashedMessage, TecApplicationLogo },

  data() {
    return {
      loading: false,
      print_details: false,
    };
  },

  //   mounted() {
  //     // JsBarcode('.barcode').init();
  //     JsBarcode('#barcode', this.sale.id, {
  //       width: 1,
  //       margin: 0,
  //       fontSize: 0,
  //       height: '20',
  //       format: 'CODE128',
  //     });
  //   },

  //   updated() {
  //     JsBarcode('#barcode', this.sale.id, {
  //       width: 1,
  //       margin: 0,
  //       fontSize: 0,
  //       height: '20',
  //       format: 'CODE128',
  //     });
  //   },
};
</script>
